import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ROUTES, generatePath } from '@/constants/routes'
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { fetchDeletedMistakes, fetchMistakes, fetchQuestionTypes, MistakeRecordListItem, MistakeStatus, restoreMistake, downloadExcel, openPdfExport } from "@/api/mistakes"
import type { ExportParams } from "@/api/mistakes"
import { useLookupStore } from "@/store/lookupStore"
import { LatexText } from "@/components/ui/LatexText"
import { ChevronDown, ChevronRight, FileText, FileSpreadsheet } from 'lucide-react'

const statusLabels: Record<MistakeStatus, string> = {
  unreviewed: "未复盘",
  reviewed: "已复盘",
  mastered: "已掌握",
}

const groupTypeLabels: Record<string, string> = {
  reading_comp: '阅读理解',
  cloze: '完形填空',
  listening: '听力',
  seven_choose_five: '七选五',
  grammar_fill: '语法填空',
}

interface GroupedMistakes {
  groupId: string
  groupType: string
  groupPassage: string | null
  items: MistakeRecordListItem[]
}

function useGroupedMistakes(items: MistakeRecordListItem[]) {
  return useMemo(() => {
    const groupMap = new Map<string, GroupedMistakes>()
    const standalone: MistakeRecordListItem[] = []

    for (const item of items) {
      if (item.groupId) {
        const existing = groupMap.get(item.groupId)
        if (existing) {
          existing.items.push(item)
        } else {
          groupMap.set(item.groupId, {
            groupId: item.groupId,
            groupType: item.groupType ?? 'unknown',
            groupPassage: item.groupPassage ?? null,
            items: [item],
          })
        }
      } else {
        standalone.push(item)
      }
    }

    // Sort items within each group by indexInGroup
    const groups = Array.from(groupMap.values()).map(g => ({
      ...g,
      items: [...g.items].sort((a, b) => (a.indexInGroup ?? 0) - (b.indexInGroup ?? 0)),
    }))

    return { groups, standalone }
  }, [items])
}

const ALL_VALUE = "ALL"

export default function MistakeListPage() {
  const [items, setItems] = useState<MistakeRecordListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [hideAnswer, setHideAnswer] = useState(true)

  const [subject, setSubject] = useState("")
  const [questionType, setQuestionType] = useState("")
  const [status, setStatus] = useState<MistakeStatus | "">("")
  const [keyword, setKeyword] = useState("")
  const [startTime, setStartTime] = useState("")
  const [endTime, setEndTime] = useState("")
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null)

  const { groups, standalone } = useGroupedMistakes(items)

  const quickFilters = [
    { label: "最近一天", days: 1 },
    { label: "最近一周", days: 7 },
    { label: "最近一月", days: 30 },
    { label: "最近3月", days: 90 },
    { label: "最近6月", days: 180 },
  ]

  function applyQuickFilter(days: number, label: string) {
    const d = new Date()
    d.setDate(d.getDate() - days)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    setStartTime(`${yyyy}-${mm}-${dd}`)
    setEndTime("")
    setActiveQuickFilter(label)
  }

  function clearTimeFilter() {
    setStartTime("")
    setEndTime("")
    setActiveQuickFilter(null)
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(items.map(i => i.id)))
    }
  }

  function buildExportParams(): ExportParams {
    const base: ExportParams = { hideAnswer: hideAnswer || undefined }
    if (selectedIds.size > 0) {
      return { ...base, ids: Array.from(selectedIds) }
    }
    return {
      ...base,
      subject: subject || undefined,
      questionType: questionType || undefined,
      status: status || undefined,
      keyword: keyword || undefined,
      startTime: startTime ? `${startTime}T00:00:00.000Z` : undefined,
      endTime: endTime ? `${endTime}T23:59:59.999Z` : undefined,
    }
  }

  async function handleExportExcel() {
    setExporting(true)
    setExportError(null)
    try {
      await downloadExcel(buildExportParams())
    } catch (err) {
      setExportError(err instanceof Error ? err.message : '导出失败')
    } finally {
      setExporting(false)
    }
  }

  async function handleExportPdf() {
    setExporting(true)
    setExportError(null)
    try {
      await openPdfExport(buildExportParams())
    } catch (err) {
      setExportError(err instanceof Error ? err.message : '导出失败')
    } finally {
      setExporting(false)
    }
  }
  const [viewDeleted, setViewDeleted] = useState(false)
  const [hasDispute, setHasDispute] = useState(false)
  const [dbQuestionTypes, setDbQuestionTypes] = useState<string[]>([])

  const { subjects, fetchAll } = useLookupStore()

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  useEffect(() => {
    fetchQuestionTypes()
      .then(setDbQuestionTypes)
      .catch(() => setDbQuestionTypes([]))
  }, [])

  const query = useMemo(
    () => ({
      page,
      pageSize,
      subject: subject || undefined,
      questionType: questionType || undefined,
      status: status || undefined,
      keyword: keyword || undefined,
      startTime: startTime ? `${startTime}T00:00:00.000Z` : undefined,
      endTime: endTime ? `${endTime}T23:59:59.999Z` : undefined,
      hasDispute: hasDispute || undefined,
    }),
    [page, pageSize, subject, questionType, status, keyword, startTime, endTime, hasDispute]
  )

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError(null)
    const loader = viewDeleted
      ? fetchDeletedMistakes({ page, pageSize })
      : fetchMistakes(query)
    loader
      .then((res) => {
        if (!mounted) return
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((err) => {
        if (!mounted) return
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!mounted) return
        setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [query, viewDeleted, page, pageSize])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">错题本</h1>
            <p className="text-sm text-slate-500">按学科、题型、时间与状态筛选你的错题</p>
          </div>
          <div className="flex items-center gap-0">
            <Button variant="default" disabled={exporting || viewDeleted} onClick={handleExportPdf}>
              <FileText className="w-4 h-4 mr-2" />
              {exporting ? "导出中..." : "导出 PDF"}
              {selectedIds.size > 0 && <span className="ml-1 text-xs opacity-70">({selectedIds.size}题)</span>}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={exporting || viewDeleted} className="ml-[-1px] px-2">
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuCheckboxItem
                  checked={hideAnswer}
                  onCheckedChange={(checked) => setHideAnswer(!!checked)}
                >
                  不导出答案
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleExportExcel}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  导出 Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {exportError && (
          <div className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-md px-3 py-2">{exportError}</div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">筛选条件</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Select
              value={subject || ALL_VALUE}
              onValueChange={(value) => setSubject(value === ALL_VALUE ? "" : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="学科" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>全部</SelectItem>
                {subjects.map((s) => (
                  <SelectItem key={s.code} value={s.name}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={questionType || ALL_VALUE}
              onValueChange={(value) => setQuestionType(value === ALL_VALUE ? "" : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="题型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>全部</SelectItem>
                {dbQuestionTypes.map((qt) => (
                  <SelectItem key={qt} value={qt}>
                    {qt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="lg:col-span-3 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-slate-600">时间筛选：</span>
                {quickFilters.map((f) => (
                  <Button
                    key={f.label}
                    variant={activeQuickFilter === f.label ? "default" : "outline"}
                    size="sm"
                    onClick={() => applyQuickFilter(f.days, f.label)}
                  >
                    {f.label}
                  </Button>
                ))}
                {(startTime || endTime) && (
                  <Button variant="ghost" size="sm" onClick={clearTimeFilter}>
                    清除
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 whitespace-nowrap">从</span>
                  <Input
                    type="date"
                    className="w-40"
                    value={startTime}
                    onChange={(e) => { setStartTime(e.target.value); setActiveQuickFilter(null) }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 whitespace-nowrap">至</span>
                  <Input
                    type="date"
                    className="w-40"
                    value={endTime}
                    onChange={(e) => { setEndTime(e.target.value); setActiveQuickFilter(null) }}
                  />
                </div>
              </div>
            </div>
            <Input placeholder="关键词" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
            <Select
              value={status || ALL_VALUE}
              onValueChange={(value) => setStatus(value === ALL_VALUE ? "" : (value as MistakeStatus))}
            >
              <SelectTrigger>
                <SelectValue placeholder="状态" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>全部</SelectItem>
                <SelectItem value="unreviewed">未复盘</SelectItem>
                <SelectItem value="reviewed">已复盘</SelectItem>
                <SelectItem value="mastered">已掌握</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <div className="flex items-center gap-4">
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <span className="text-sm text-slate-600">有争议</span>
            <Switch checked={hasDispute} onCheckedChange={setHasDispute} />
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <span className="text-sm text-slate-600">查看回收站</span>
            <Switch checked={viewDeleted} onCheckedChange={setViewDeleted} />
          </div>
          {!viewDeleted && items.length > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2">
              <Checkbox
                checked={selectedIds.size === items.length && items.length > 0}
                onCheckedChange={toggleSelectAll}
              />
              <span className="text-sm text-slate-600">
                全选{selectedIds.size > 0 && ` (已选 ${selectedIds.size} 题)`}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {loading && <div className="text-sm text-slate-500">加载中...</div>}
          {error && <div className="text-sm text-red-500">{error}</div>}
          {!loading && !error && items.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-sm text-slate-500">暂无错题记录</CardContent>
            </Card>
          )}
          {/* Grouped mistakes (reading/cloze/etc.) */}
          {!loading &&
            !error &&
            groups.map((group) => {
              const isCollapsed = collapsedGroups.has(group.groupId)
              const groupLabel = groupTypeLabels[group.groupType] ?? group.groupType
              const unreviewedCount = group.items.filter(i => i.status === 'unreviewed').length
              return (
                <Card key={group.groupId} className="overflow-hidden border-l-4 border-l-blue-500 transition-shadow hover:shadow-md">
                  {/* Group header */}
                  <button
                    className="w-full text-left"
                    onClick={() => setCollapsedGroups(prev => {
                      const next = new Set(prev)
                      if (next.has(group.groupId)) next.delete(group.groupId)
                      else next.add(group.groupId)
                      return next
                    })}
                  >
                    <CardContent className="py-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {isCollapsed
                            ? <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                            : <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                          }
                          <Badge variant="outline" className="shrink-0 border-blue-200 bg-blue-50 text-blue-700">
                            {groupLabel}
                          </Badge>
                          <span className="text-xs text-slate-500 shrink-0">{group.items.length} 道错题</span>
                          {unreviewedCount > 0 && (
                            <Badge variant="secondary" className="shrink-0 text-xs">{unreviewedCount} 待复盘</Badge>
                          )}
                        </div>
                      </div>
                      {group.groupPassage && (
                        <div className="ml-6 rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
                          <div className="flex items-start gap-1.5">
                            <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400 mt-0.5" />
                            <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                              {group.groupPassage}
                            </p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </button>
                  {/* Sub-questions */}
                  {!isCollapsed && (
                    <div className="border-t border-slate-100 divide-y divide-slate-100">
                      {group.items.map((item) => (
                        <div key={item.id} className="px-4 py-3 hover:bg-slate-50/50 transition-colors">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {!viewDeleted && (
                                <Checkbox
                                  checked={selectedIds.has(item.id)}
                                  onCheckedChange={() => toggleSelect(item.id)}
                                  className="shrink-0"
                                />
                              )}
                              <Link
                                to={generatePath(ROUTES.MISTAKE_DETAIL, { mistakeId: item.id })}
                                className="flex items-center gap-2 text-sm font-medium text-slate-900 hover:underline min-w-0"
                              >
                                <span className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                                  {(item.indexInGroup ?? 0) + 1}
                                </span>
                                <span className="truncate">
                                  <LatexText inline>{item.questionStem || "[公式/图片题]"}</LatexText>
                                </span>
                              </Link>
                            </div>
                            <Badge variant="outline" className="shrink-0">{statusLabels[item.status]}</Badge>
                          </div>
                          <div className="mt-1 ml-7 flex flex-wrap gap-3 text-xs text-slate-500">
                            <span>错题次数：{item.wrongCount}</span>
                            <span>最近：{item.lastWrongAt ? new Date(item.lastWrongAt).toLocaleDateString() : "—"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )
            })
          }
          {/* Standalone mistakes (no group) */}
          {!loading &&
            !error &&
            standalone.map((item) => (
              <Card className="transition-shadow hover:shadow-md" key={item.id}>
                <CardContent className="py-4 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {!viewDeleted && (
                        <Checkbox
                          checked={selectedIds.has(item.id)}
                          onCheckedChange={() => toggleSelect(item.id)}
                          className="shrink-0"
                        />
                      )}
                      <Link to={generatePath(ROUTES.MISTAKE_DETAIL, { mistakeId: item.id })} className="text-sm font-medium text-slate-900 hover:underline">
                        <LatexText inline>{item.questionStem || "[公式/图片题]"}</LatexText>
                      </Link>
                    </div>
                    <Badge variant="outline">{viewDeleted ? "已删除" : statusLabels[item.status]}</Badge>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
                    <div className="flex flex-wrap gap-3">
                      <span>学科：{item.subject || "未标注"}</span>
                      <span>题型：{item.questionType || "未标注"}</span>
                      <span>错题次数：{item.wrongCount}</span>
                      <span>最近错题：{item.lastWrongAt ? new Date(item.lastWrongAt).toLocaleString() : "—"}</span>
                    </div>
                    {viewDeleted && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            await restoreMistake(item.id)
                            const res = await fetchDeletedMistakes({ page, pageSize })
                            setItems(res.items)
                            setTotal(res.total)
                          } catch (err) {
                            setError(err instanceof Error ? err.message : String(err))
                          }
                        }}
                      >
                        恢复
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          }
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500">
            第 {page} / {totalPages} 页，共 {total} 条
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
              上一页
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
              下一页
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
